/**
 * A panel representing a single position, including summary data,
 * associated orders, and options to create new orders.
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
    E,
    usePortfolio,
    useQuotes,
    Position,
    wsClient,
    ordersManager
} from '../../logic/imports';
import { OrdersDisplay } from './OrdersDisplay';
import { NewOrderSelect } from './NewOrderSelect';
import { OrderDetails } from './OrderDetails';
import { showDimmer } from '../app/Dimmer'


interface PositionProps {
    position : Position;
}

// Helper function to calculate DTE
function calculateDTE(expirationDateStr: string): number {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Parse date string as local time, not UTC
    const [year, month, day] = expirationDateStr.split('-').map(Number);
    const expDate = new Date(year, month - 1, day, 0, 0, 0, 0);

    const diffTime = expDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return Math.max(0, diffDays);
}

export const PositionComp: React.FC<PositionProps> = (props) => { 
    const { position } = props;
    // Always get the latest orders from context
    const { orders } = usePortfolio();
    // Quote data
    const { quotes, requestQuote, releaseQuote } = useQuotes();

    const [isDimmed, setIsDimmed] = useState(false);
    const [newOrderSide, setNewOrderSide] = useState<E.OrderSide>(E.OrderSide.BUY);
    const [newOrderType, setNewOrderType] = useState<E.OrderType | null>(null);
    const [gainDisplayType, setGainDisplayType] = useState<'dollar' | 'percentage'>('percentage'); // 'dollar' or 'percentage'
    const [gainValue, setGainValue] = useState<number>(position.stats?.openPnL || 0);
    const [lastPrice, setLastPrice] = useState<number>(position.lastPrice || 0);

    // Find all the orders for this position using useMemo to ensure updates
    const positionOrders = useMemo(() => {
        const filtered = new Map();

        if (!orders) return filtered;

        orders.forEach(order => {
            if (order.instrument.symbol === position.option.symbol) {
                filtered.set(order.id, order);
            }
        });

        return filtered;
    }, [orders, position.option.symbol]);

    useEffect(() => {
        const quote = quotes.find(q => q.instrument.symbol === position.option.symbol);
        if (quote) {
            setLastPrice(quote.last);
        }
    }, [quotes]);

    // Subscribe to quotes on mount, release on unmount
    useEffect(() => {
        const subId = crypto.randomUUID().substring(0, 3);
        const tickerSymbol = position.option.symbol;
        const symbolInstrument = position.option.underlying!.symbol;
        
        requestQuote(tickerSymbol, `PositionComp-${subId}`);
        requestQuote(symbolInstrument, `PositionComp-${subId}`);

        return () => {
            releaseQuote(tickerSymbol, `PositionComp-${subId}`);
            releaseQuote(symbolInstrument, `PositionComp-${subId}`);
        };
    }, []);

    const onDimPanel = (enable: boolean) => {
        setIsDimmed(enable);
    }

    const onFlattenClicked = async () => {
        let res = await wsClient.sendRequest(
            E.WsRoute.POSITIONS,
            E.WsPositionsCommand.FLATTEN,
            position.id
        );
        if (!res.success) {
            showDimmer('Error flattening position: ' + res.message, true);
            return;
        }
        const liqOrder = res.data;
        // Ingest the liquidation order into the orders manager
        ordersManager.handleOrderUpdate(liqOrder);
    }

    const onRemoveClicked = async () => {
        try {
            await wsClient.sendRequest(
                E.WsRoute.POSITIONS,
                E.WsPositionsCommand.REMOVE,
                position.option.symbol
            );
        } catch (error: any) {
            showDimmer('Error removing position: ' + error.message, true);
        }
    }

    const dte = position.option.expiration ? calculateDTE(position.option.expiration) : 0;
    
    const getGainValue = () => {
        if (!lastPrice || !position.stats)
            return undefined;

        const currentValue = lastPrice * 100 * position.contractsHeld;
        const totalCost = position.stats.totalCost;

        if (gainDisplayType === 'dollar') {
            return currentValue - totalCost;
        } else {
            return ((currentValue / totalCost) - 1) * 100;
        }
    }

    const formatGainValue = () => {
        const useClosedPnl = position.contractsHeld === 0;
        const valueToUse = useClosedPnl ? getClosedPnl() : getGainValue();

        if (valueToUse === undefined) {
            return (
                <h3>---</h3>
            );
        }

        const positiveStyle = { color: '#65ff65ff', margin: 0, fontWeight: 'normal', textShadow: '0 0 5px #000000ff' };
        const negativeStyle = { color: '#ff3d3dff', margin: 0, fontWeight: 'normal', textShadow: '0 0 5px #000000ff' };

        const plusMinus = valueToUse >= 0 ? '+ ' : '- ';
        const prefix = gainDisplayType === 'dollar' ? '$' : '';
        const suffix = gainDisplayType === 'dollar' ? '' : '%';
        
        return (
            valueToUse >= 0 ? (
                <h3 style={positiveStyle}>
                    {plusMinus}{prefix}{(valueToUse).toFixed(2)}{suffix}
                </h3>
            ) : (
                <h3 style={negativeStyle}>
                    {plusMinus}{prefix}{(Math.abs(valueToUse)).toFixed(2)}{suffix}
                </h3>
            )
        )
    }

    const getClosedPnl = () => {
        // Calculate closed P&L from order fill prices
        let totalContracts = 0;
        let totalSellValue = 0;

        positionOrders.forEach(order => {
            if (order.status !== E.OrderStatus.FILLED || order.side !== E.OrderSide.SELL)
                return;

            const filledQty = order.stats?.filledQuantity ?? 0;
            const avgFill = order.stats?.avgFillPrice ?? 0;
            totalSellValue += filledQty * avgFill * 100;
            totalContracts += filledQty;
        });

        const avgUnitCost = position.stats?.avgUnitCost ?? 0;
        const totalPositionCost = avgUnitCost * 100 * totalContracts;
        if (totalPositionCost === 0) return 0;
        const pnl = totalSellValue - totalPositionCost;
        const pnlPercentage = ((totalSellValue / totalPositionCost) - 1) * 100;

        return gainDisplayType === 'dollar' ? pnl : pnlPercentage;
    }

    return (
        <div className="positionComp" style={{ minHeight: '200px' }}>

            {/* Position Header */}
            <div className="quickPositionHeader">
                {/* Symbol */}
                <h2>
                    {position.option.underlying?.symbol} ${position.option.strike} {position.option.type}
                    {/* DTE */}
                    {dte > -1 && (
                        <span className="quickPositionHeader-zeroDteLabel">({dte} DTE)</span>
                    )}
                </h2>

                {/* Quantity */}
                <div style={{ wordSpacing: '5px', lineHeight: '1.5rem' }}>
                    {/* // TODO : Show "Position Opening" if quantity is 0 but there are open orders
                    // TODO : Show "Position Closed" if quantity is 0 and there were prior filled contracts */}
                    {position.contractsHeld > 0 && (
                        <>
                            {position.contractsHeld} Contract{position.contractsHeld !== 1 ? 's ' : ' '}
                            @ {(position.stats?.avgUnitCost ?? 0).toFixed(2)} ➜ {lastPrice.toFixed(2)}
                        </>
                    )}
                </div>

                {/* Percent Gain / Dollar Gain Toggle */}
                {position.contractsHeld > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    
                        {/* P&L Type Toggle */}
                        <button
                            onClick={() => setGainDisplayType(gainDisplayType === 'dollar' ? 'percentage' : 'dollar')}
                            className='strike-limit-button'
                            style={{
                                width: '50px',
                                padding: '2px',
                                fontSize: '0.8rem',
                                cursor: 'pointer',
                            }}
                            title="Toggle between percentage and dollar gain"
                        >
                            $ / %
                        </button>

                        {/* Label */}
                        <p>
                            {position.contractsHeld > 0 ? 'Open P&L' : 'Net Closed P&L'}
                        </p>

                        {/* P&L Number */}
                        {formatGainValue()}
                        
                    </div>
                )}

                {/* Avg Price */}
                <div>
                    
                </div>
            </div>

            {/* Position Data Table */}
            {/* <PositionDetails position={position} lastPrice={lastPrice} /> */}

            {/* Existing orders box */}
            <OrdersDisplay key={position.option.symbol} orders={positionOrders} position={position} />
            
            {/* Normal Mode - show new order buttons */}
            {newOrderType === null && (
                <NewOrderSelect
                    instrument={position.option}
                    onDimPosition={onDimPanel}
                    onOrderTypeSelected={(side, type) => {
                        setNewOrderSide(side);
                        setNewOrderType(type);
                        setIsDimmed(false);
                    }}
                />
            )}

            {/* New Order Mode - show order entry panel*/}
            {newOrderType !== null && (
                <OrderDetails
                    position={position}
                    side={newOrderSide}
                    type={newOrderType}
                    onClose={() => setNewOrderType(null)}
                />
            )}

            {/* Hide button */}
            {position.contractsHeld === 0 && (
                <button
                    className="positionHideButton"
                    onClick={onRemoveClicked}
                    title="Hide this empty position"
                >
                    Remove
                </button>
            )}

            {/* Flatten button */}
            {position.contractsHeld > 0 && (
                <button
                    className="positionFlattenButton"
                    onClick={() => onFlattenClicked()}
                    title="Liquidate this position with a Market order"
                >
                    Flatten
                </button>
            )}

            {/* Posiion Dimmer */}
            {isDimmed && <div className="positionDimmer"></div>}

        </div>
    );
}